import React, { useMemo, useState } from 'react';
import RecCard from './RecCard';
import { sortByPriority } from '../../utils/performanceCards';

/**
 * A tab's "More (N)": the findings utils/findingTier.js puts behind the main
 * feed — on Diagnostics entity detail that is limited-but-serving, keyword-level
 * rows and minor issues; on Optimization unsupported or unestimated ideas and
 * chores; on Budget & pacing reads with no measured gap.
 *
 * One button with its own count, at the foot of the feed. Collapsed by default;
 * expanded, the cards are grouped by campaign, then by kind of finding, each
 * card collapsed as everywhere else. Dismiss works exactly as in the feed: the
 * card leaves at once, and the server's record takes over on the refetch.
 *
 * The open/closed state is component state only — this component is mounted
 * once per tab, so each tab remembers its own, and a reload starts closed.
 */

/** The group heading for a kind of finding. Other actions are named in words. */
const KIND_LABEL = {
  delivery_health: 'Campaign delivery reads',
  delivery_blocked: 'Asset groups, ad groups and keywords',
  delivery_waiting: 'Waiting on Google',
  blocking_issue: 'Campaign issues',
  negative_keyword_conflict: 'Negative-keyword conflicts',
  tracking: 'Account reviews',
};
const KIND_ORDER = Object.keys(KIND_LABEL);
const kindOf = (c) => String(c.action || 'other');
const kindLabel = (k) => {
  if (KIND_LABEL[k]) return KIND_LABEL[k];
  const words = k.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Other findings';
};

export default function MoreFindings({
  cards = [],
  note = null,
  onlyAuto = false,   // the feed's "Can apply" toggle, so More narrows with it
  onApply,
  applying = {},
  onDismiss,
  reasonSets = {},
  notes = {},
}) {
  const [open, setOpen] = useState(false);
  // Optimistic, as in CardFeed: a dismissed card leaves the list at once, and
  // an applied one renders as applied (no buttons) until the refetch moves it
  // into the Applied section.
  const [justDismissed, setJustDismissed] = useState({});
  const [justApplied, setJustApplied] = useState({});
  const live = useMemo(
    () => cards.filter((c) => !justDismissed[c.id] && (!onlyAuto || c.auto)),
    [cards, justDismissed, onlyAuto],
  );

  const groups = useMemo(() => {
    const byCamp = new Map();
    live.forEach((c) => {
      const key = c.camp || 'account';
      if (!byCamp.has(key)) byCamp.set(key, { key, name: c.campName || 'Account', kinds: new Map() });
      const g = byCamp.get(key);
      const k = kindOf(c);
      if (!g.kinds.has(k)) g.kinds.set(k, []);
      g.kinds.get(k).push(c);
    });
    // Campaigns by name; kinds in a fixed order; cards by priority within each.
    // Plain code-point comparison, not localeCompare: same order on every
    // runtime and locale.
    return [...byCamp.values()]
      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      .map((g) => ({
        ...g,
        kinds: [...g.kinds.entries()]
          .sort(([a], [b]) => ((KIND_ORDER.indexOf(a) + 1 || 99) - (KIND_ORDER.indexOf(b) + 1 || 99))
            || (a < b ? -1 : a > b ? 1 : 0))
          .map(([k, list]) => ({ kind: k, cards: sortByPriority(list) })),
      }));
  }, [live]);

  if (!live.length) return null;

  return (
    <div className="dgn-more">
      <button
        type="button"
        className="btn btn-sec btn-sm dgn-more-btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        More ({live.length})
        <span aria-hidden="true" className="material-icons rc-chev">{open ? 'expand_less' : 'expand_more'}</span>
      </button>
      {open ? (
        <div className="dgn-more-body">
          {note ? <p className="dgn-more-note">{note}</p> : null}
          {groups.map((g) => (
            <section key={g.key} className="dgn-more-camp" aria-label={g.name}>
              <div className="dgn-more-camp-h">{g.name}</div>
              {g.kinds.map(({ kind, cards: list }) => (
                <div key={kind} className="dgn-more-kind">
                  <div className="dgn-more-kind-h">{kindLabel(kind)} · {list.length}</div>
                  {list.map((c) => (
                    <RecCard
                      key={c.id}
                      card={c}
                      applied={Boolean(justApplied[c.id])}
                      appliedEdit={justApplied[c.id] || null}
                      onApply={async (card) => {
                        const out = await onApply?.(card);
                        if (out?.ok) {
                          setJustApplied((a) => ({
                            ...a,
                            [card.id]: { status: 'applying', at: new Date().toISOString(), editMessageId: out.messageId || null },
                          }));
                        }
                        return out;
                      }}
                      applying={Boolean(applying[c.id])}
                      reasonSets={reasonSets}
                      notes={notes}
                      onDismiss={async (card, payload) => {
                        const rec = await onDismiss?.(card, payload);
                        setJustDismissed((d) => ({ ...d, [card.id]: rec || true }));
                        return rec;
                      }}
                    />
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
